import React, { useState } from "react";
import {
  ChevronDown, ChevronUp, Edit2, Trash2, AlertTriangle,
  CheckCircle2, Clock, Shield, MessageSquare, Save, X, Tag
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-red-50 text-red-600 border-red-200",
  medium: "bg-orange-50 text-orange-600 border-orange-200",
  low: "bg-yellow-50 text-yellow-600 border-yellow-200",
  info: "bg-slate-50 text-slate-600 border-slate-200",
};

const ENTRY_TYPE_ICONS = {
  observation: CheckCircle2,
  decision: Shield,
  issue: AlertTriangle,
  assumption: MessageSquare,
  activity: Clock,
};

export default function DiaryEntryCard({ entry, projectId, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [followupAnswer, setFollowupAnswer] = useState("");
  const [answering, setAnswering] = useState(false);
  const [editData, setEditData] = useState({});

  const s = entry.structured || {};
  const severity = s.severity || "info";
  const Icon = ENTRY_TYPE_ICONS[entry.entry_type] || Clock;

  const handleFollowup = async () => {
    if (!followupAnswer.trim()) return;
    setAnswering(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/projects/${projectId}/diary/${entry.id}/followup`,
        { answer: followupAnswer }
      );
      onUpdate(res.data);
      setFollowupAnswer("");
      toast.success("Follow-up answered, entry updated");
    } catch {
      toast.error("Failed to process follow-up");
    } finally {
      setAnswering(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/projects/${projectId}/diary/${entry.id}`,
        editData
      );
      onUpdate(res.data);
      setEditing(false);
      toast.success("Entry updated");
    } catch {
      toast.error("Failed to update entry");
    }
  };

  const startEdit = () => {
    setEditData({
      entry_type: entry.entry_type,
      linked_test_cases: entry.linked_test_cases || [],
      linked_requirements: entry.linked_requirements || [],
      safety_relevant: entry.safety_relevant,
      tags: entry.tags || [],
    });
    setEditing(true);
  };

  return (
    <Card
      className={`bg-white border rounded-sm shadow-sm transition-all duration-200 ${
        entry.safety_relevant ? "border-l-2 border-l-red-500" : "border-slate-200"
      }`}
      data-testid={`diary-entry-${entry.id}`}
    >
      <CardContent className="p-0">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50/50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Icon className="w-4 h-4 text-[#003057] shrink-0" strokeWidth={1.5} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-700 truncate">
                {s.test_activity || entry.raw_input?.slice(0, 80) || "Diary entry"}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-slate-400 font-mono">
                  {new Date(entry.created_at).toLocaleString()}
                </span>
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-slate-300 text-slate-500">
                  {entry.entry_type}
                </Badge>
                <Badge className={`text-[9px] h-4 px-1 border ${SEVERITY_STYLES[severity]}`}>
                  {severity}
                </Badge>
                {entry.safety_relevant && (
                  <Badge className="text-[9px] h-4 px-1 bg-red-100 text-red-700 border-red-200">
                    Safety
                  </Badge>
                )}
                {entry.source !== "text" && (
                  <Badge variant="outline" className="text-[9px] h-4 px-1 border-blue-200 text-blue-600">
                    {entry.source === "voice_whisper" ? "Whisper" : "Voice"}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {entry.ai_followup && !entry.ai_followup_answered && (
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" title="Follow-up pending" />
            )}
            {expanded ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div className="border-t border-slate-100 px-4 py-3 space-y-3">
            {/* Structured fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["Test Activity", s.test_activity],
                ["Observations", s.observations],
                ["Deviations", s.deviations],
                ["Decisions Taken", s.decisions_taken],
                ["Assumptions", s.assumptions],
                ["Open Issues", s.open_issues],
                ["Next Actions", s.next_actions],
              ]
                .filter(([, v]) => v)
                .map(([label, value], i) => (
                  <div key={i} className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {label}
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed">{value}</p>
                  </div>
                ))}
            </div>

            {/* Traceability */}
            {(entry.linked_test_cases?.length > 0 || entry.linked_requirements?.length > 0) && (
              <>
                <Separator />
                <div className="flex flex-wrap gap-1.5">
                  {entry.linked_test_cases?.map((tc, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] h-5 border-blue-200 text-blue-600">
                      TC: {tc}
                    </Badge>
                  ))}
                  {entry.linked_requirements?.map((req, i) => (
                    <Badge key={i} variant="outline" className="text-[10px] h-5 border-purple-200 text-purple-600">
                      REQ: {req}
                    </Badge>
                  ))}
                </div>
              </>
            )}

            {/* AI Follow-up */}
            {entry.ai_followup && (
              <>
                <Separator />
                <div className="bg-[#003057]/5 rounded-sm p-3 space-y-2">
                  <p className="text-xs font-semibold text-[#003057] flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> AI Follow-up Question
                  </p>
                  <p className="text-sm text-slate-700">{entry.ai_followup}</p>
                  {!entry.ai_followup_answered && (
                    <div className="flex gap-2 mt-2">
                      <Input
                        data-testid={`followup-input-${entry.id}`}
                        value={followupAnswer}
                        onChange={(e) => setFollowupAnswer(e.target.value)}
                        placeholder="Type your answer..."
                        className="rounded-sm text-sm"
                        onKeyDown={(e) => e.key === "Enter" && handleFollowup()}
                      />
                      <Button
                        data-testid={`followup-submit-${entry.id}`}
                        onClick={handleFollowup}
                        disabled={answering || !followupAnswer.trim()}
                        size="sm"
                        className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm shrink-0"
                      >
                        {answering ? "..." : "Answer"}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Edit mode */}
            {editing ? (
              <>
                <Separator />
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                      Linked Test Cases (comma-separated)
                    </p>
                    <Input
                      value={(editData.linked_test_cases || []).join(", ")}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          linked_test_cases: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })
                      }
                      className="rounded-sm text-sm"
                      placeholder="TC-001, TC-002"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                      Linked Requirements (comma-separated)
                    </p>
                    <Input
                      value={(editData.linked_requirements || []).join(", ")}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          linked_requirements: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })
                      }
                      className="rounded-sm text-sm"
                      placeholder="REQ-101, REQ-102"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                      Tags (comma-separated)
                    </p>
                    <Input
                      value={(editData.tags || []).join(", ")}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })
                      }
                      className="rounded-sm text-sm"
                      placeholder="regression, cold-start"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-600 flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={editData.safety_relevant || false}
                        onChange={(e) => setEditData({ ...editData, safety_relevant: e.target.checked })}
                        className="rounded"
                      />
                      Safety Relevant
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveEdit} size="sm" className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm">
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button onClick={() => setEditing(false)} variant="outline" size="sm" className="rounded-sm">
                      <X className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 pt-1">
                <Button
                  onClick={startEdit}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-slate-500 hover:text-[#003057] h-7"
                  data-testid={`edit-entry-${entry.id}`}
                >
                  <Edit2 className="w-3 h-3 mr-1" /> Edit traceability
                </Button>
                <Button
                  onClick={() => onDelete(entry.id)}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-slate-500 hover:text-red-500 h-7"
                  data-testid={`delete-entry-${entry.id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            )}

            {/* Raw input */}
            <details className="text-xs text-slate-400">
              <summary className="cursor-pointer hover:text-slate-500 transition-colors">
                Raw input
              </summary>
              <p className="mt-1 font-mono text-[11px] text-slate-500 bg-slate-50 p-2 rounded-sm whitespace-pre-wrap">
                {entry.raw_input}
              </p>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
